import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Search, Upload, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SectionHeader } from '@/components/layout/SectionHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { usePipelines, useDeletePipeline, useExecutePipeline } from './hooks/use-pipelines';
import { useUpdatePipelineSchedule, useDeletePipelineSchedule } from './hooks/use-pipeline-schedule';
import { PipelineCard } from './components/PipelineCard';
import { PipelineList } from './components/PipelineList';
import { PipelineEmptyState } from './components/PipelineEmptyState';
import { CreatePipelineDialog } from './components/CreatePipelineDialog';
import { DuplicatePipelineDialog } from './components/DuplicatePipelineDialog';
import { ScheduleEditorDialog } from './components/ScheduleEditorDialog';
import { ScheduleConfig } from './types/pipeline';
import { ViewToggle } from './components/ViewToggle';
import { BulkActionsBar } from './components/BulkActionsBar';
import { AdvancedFilters, getAdvancedFilterCount, AdvancedFiltersState } from './components/AdvancedFilters';
import { PipelineExportDialog, PipelineImportDialog } from './components/PipelineExport';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type ViewMode = 'grid' | 'list';
type SortOption = 'lastRun' | 'name' | 'created';
type StatusFilter = 'all' | 'healthy' | 'warning' | 'error';

const STATUS_SEGMENTS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'healthy', label: 'Healthy' },
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Error' },
];

const VIEW_MODE_STORAGE_KEY = 'pipeline-dashboard-view-mode';

function getStoredViewMode(): ViewMode {
  try {
    const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return stored === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
}

function setStoredViewMode(mode: ViewMode) {
  try {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    // Ignore storage errors
  }
}

export default function PipelineDashboard() {
  const navigate = useNavigate();
  const { data, isLoading, refetch } = usePipelines();
  const deleteMutation = useDeletePipeline();
  const executeMutation = useExecutePipeline();

  // Schedule mutations - will be accessed via schedulePipeline.id
  const [schedulePipelineId, setSchedulePipelineId] = useState<string | null>(null);
  const updateScheduleMutation = useUpdatePipelineSchedule(schedulePipelineId || '');
  const deleteScheduleMutation = useDeletePipelineSchedule(schedulePipelineId || '');

  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>(getStoredViewMode);
  const [sortBy, setSortBy] = useState<SortOption>('lastRun');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFiltersState>({
    dateFrom: '',
    dateTo: '',
    tags: [],
  });

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deletePipeline, setDeletePipeline] = useState<{ id: string; name: string } | null>(null);
  const [duplicatePipeline, setDuplicatePipeline] = useState<{ id: string; name: string } | null>(null);
  const [schedulePipeline, setSchedulePipeline] = useState<{ id: string; name: string; schedule?: ScheduleConfig } | null>(null);
  const [bulkDeletePipeline, setBulkDeletePipeline] = useState<string[] | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Bulk selection state
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const pipelines = data?.pipelines || [];

  // Persist view mode preference
  useEffect(() => {
    setStoredViewMode(viewMode);
  }, [viewMode]);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchQuery, statusFilter, sortBy, advancedFilters]);

  // Filter and sort pipelines
  const processedPipelines = useMemo(() => {
    let result = [...pipelines];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((pipeline) =>
        pipeline.name.toLowerCase().includes(query) ||
        pipeline.description?.toLowerCase().includes(query)
      );
    }

    // Apply status filter (health-based)
    if (statusFilter !== 'all') {
      result = result.filter(() => {
        // TODO: Filter by actual health status from execution history
        return statusFilter === 'healthy';
      });
    }

    // Apply advanced filters
    if (advancedFilters.dateFrom) {
      const fromDate = new Date(advancedFilters.dateFrom);
      result = result.filter((p) => new Date(p.createdAt) >= fromDate);
    }
    if (advancedFilters.dateTo) {
      const toDate = new Date(advancedFilters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter((p) => new Date(p.createdAt) <= toDate);
    }
    if (advancedFilters.tags.length > 0) {
      // TODO: Filter by tags when tags are added to pipeline model
      // For now, this filter has no effect
    }

    // Apply sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'created':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'lastRun':
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        default:
          return 0;
      }
    });

    return result;
  }, [pipelines, searchQuery, statusFilter, sortBy, advancedFilters]);

  // Transform pipeline data to card format
  const pipelineData = useMemo(() =>
    processedPipelines.map((pipeline) => ({
      id: pipeline.id,
      name: pipeline.name,
      description: pipeline.description,
      lastExecution: pipeline.lastExecution,                   // ✅ Use actual data from API
      nodeCount: pipeline.nodeCount || 0,                      // ✅ Use actual data from API
      healthStatus: pipeline.healthStatus || ('healthy' as const), // ✅ Use actual data from API
      schedule: pipeline.schedule,                             // ✅ Include schedule info
      nextRunAt: pipeline.nextRunAt,                          // ✅ Include next run time
    })), [processedPipelines]
  );

  // Toggle bulk mode
  const toggleBulkMode = () => {
    setBulkMode(!bulkMode);
    setSelectedIds(new Set());
  };

  // Toggle selection for single item
  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectedIds.size === pipelineData.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pipelineData.map((p) => p.id)));
    }
  };

  const handleCreateNew = () => {
    setCreateDialogOpen(true);
  };

  const handleEdit = (id: string) => {
    navigate(`/pipelines/${id}`);
  };

  const handleDuplicate = (id: string) => {
    const pipeline = pipelines.find((p) => p.id === id);
    if (pipeline) {
      setDuplicatePipeline({ id: pipeline.id, name: pipeline.name });
    }
  };

  const handleDelete = (id: string) => {
    const pipeline = pipelines.find((p) => p.id === id);
    if (pipeline) {
      setDeletePipeline({ id: pipeline.id, name: pipeline.name });
    }
  };

  const handleSchedule = (id: string) => {
    const pipeline = pipelines.find((p) => p.id === id);
    if (pipeline) {
      setSchedulePipelineId(id);
      setSchedulePipeline({
        id: pipeline.id,
        name: pipeline.name,
        schedule: pipeline.scheduleConfig,
      });
    }
  };

  const confirmDelete = () => {
    if (deletePipeline) {
      deleteMutation.mutate(deletePipeline.id, {
        onSuccess: () => {
          setDeletePipeline(null);
          setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(deletePipeline.id);
            return next;
          });
        },
      });
    }
  };

  const confirmBulkDelete = () => {
    if (bulkDeletePipeline && bulkDeletePipeline.length > 0) {
      Promise.all(
        bulkDeletePipeline.map((id) =>
          deleteMutation.mutateAsync(id, { onSuccess: () => {} })
        )
      ).then(() => {
        setBulkDeletePipeline(null);
        setSelectedIds(new Set());
        refetch();
      });
    }
  };

  const handleRun = (id: string) => {
    executeMutation.mutate(id);
  };

  const handleBulkExport = () => {
    setExportDialogOpen(true);
  };

  const handleBulkDelete = () => {
    setBulkDeletePipeline(Array.from(selectedIds));
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleClearAdvancedFilters = () => {
    setAdvancedFilters({ dateFrom: '', dateTo: '', tags: [] });
  };

  const isLoadingData = isLoading || deleteMutation.isPending;
  const advancedFilterCount = getAdvancedFilterCount(advancedFilters);

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Pipeline Command Hub"
        subtitle="Track orchestration status, triage failed jobs, and launch new pipelines from one control plane."
        metric={`${pipelineData.length} tracked`}
        actions={(
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={() => refetch()}
              disabled={isLoadingData}
              aria-label="Refresh pipelines"
            >
              <RefreshCw className={`h-4 w-4 ${isLoadingData ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              onClick={() => setImportDialogOpen(true)}
            >
              <Upload className="h-4 w-4 mr-2" />
              Import
            </Button>
            <Button onClick={handleCreateNew}>
              <Plus className="h-4 w-4 mr-2" />
              New Pipeline
            </Button>
          </>
        )}
      />

      {/* Filters Bar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative w-full sm:max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search pipelines..."
              className="pl-9"
            />
          </div>

          <div
            data-testid="pipeline-status-segments"
            className="inline-flex items-center gap-1 rounded-full border bg-surface-elevated p-1"
          >
            {STATUS_SEGMENTS.map((segment) => (
              <Button
                key={segment.value}
                type="button"
                variant={statusFilter === segment.value ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setStatusFilter(segment.value)}
                className="rounded-full px-3"
              >
                {segment.label}
              </Button>
            ))}
          </div>

          {/* Advanced Filters */}
          <AdvancedFilters
            filters={advancedFilters}
            onChange={setAdvancedFilters}
            onClear={handleClearAdvancedFilters}
            activeFilterCount={advancedFilterCount}
          />

          {/* Sort */}
          <Select value={sortBy} onValueChange={(value: SortOption) => setSortBy(value)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="lastRun">Last Run</SelectItem>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="created">Created</SelectItem>
            </SelectContent>
          </Select>

          {/* View Toggle */}
          <ViewToggle mode={viewMode} onChange={setViewMode} />

          {/* Bulk Mode Toggle */}
          <Button
            variant={bulkMode ? 'default' : 'outline'}
            size="sm"
            onClick={toggleBulkMode}
          >
            {bulkMode ? (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Done
              </>
            ) : (
              'Select'
            )}
          </Button>
        </div>

        {/* Result Count */}
        <div className="text-sm text-muted-foreground">
          {pipelineData.length} pipeline{pipelineData.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Bulk Selection Header */}
      {bulkMode && (
        <div className="flex items-center gap-4 px-4 py-2 rounded-md border bg-muted/50">
          <Checkbox
            checked={selectedIds.size === pipelineData.length && pipelineData.length > 0}
            onCheckedChange={toggleSelectAll}
          />
          <span className="text-sm text-muted-foreground">
            {selectedIds.size === 0
              ? 'Select pipelines to perform bulk actions'
              : `${selectedIds.size} pipeline${selectedIds.size !== 1 ? 's' : ''} selected`}
          </span>
        </div>
      )}

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedIds.size}
        onClearSelection={handleClearSelection}
        onBulkDelete={handleBulkDelete}
        onBulkExport={handleBulkExport}
        isPending={deleteMutation.isPending}
      />

      {/* Content */}
      {isLoadingData ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground">Loading pipelines...</span>
        </div>
      ) : pipelineData.length === 0 ? (
        searchQuery || statusFilter !== 'all' || advancedFilterCount > 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground">
              No pipelines found matching your filters
            </p>
            <div className="flex items-center justify-center gap-2 mt-4">
              {searchQuery && (
                <Button variant="link" onClick={() => setSearchQuery('')}>
                  Clear search
                </Button>
              )}
              {(statusFilter !== 'all' || advancedFilterCount > 0) && (
                <>
                  {searchQuery && <span className="text-muted-foreground">•</span>}
                  <Button variant="link" onClick={() => { setStatusFilter('all'); handleClearAdvancedFilters(); }}>
                    Clear all filters
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : (
          <PipelineEmptyState onCreateNew={handleCreateNew} />
        )
      ) : viewMode === 'grid' ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {pipelineData.map((pipeline) => (
            <PipelineCard
              key={pipeline.id}
              pipeline={pipeline}
              onRun={handleRun}
              onEdit={handleEdit}
              onSchedule={handleSchedule}
              onDuplicate={handleDuplicate}
              onDelete={handleDelete}
              isSelected={selectedIds.has(pipeline.id)}
              onToggleSelect={bulkMode ? toggleSelection : undefined}
              bulkMode={bulkMode}
            />
          ))}
        </div>
      ) : (
        <PipelineList
          pipelines={pipelineData}
          onRun={handleRun}
          onEdit={handleEdit}
          onSchedule={handleSchedule}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          sortBy={sortBy}
          onSortChange={setSortBy}
        />
      )}

      {/* Create Pipeline Dialog */}
      <CreatePipelineDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {/* Duplicate Pipeline Dialog */}
      <DuplicatePipelineDialog
        open={!!duplicatePipeline}
        onOpenChange={(open) => !open && setDuplicatePipeline(null)}
        sourcePipeline={duplicatePipeline}
      />

      {/* Schedule Editor Dialog */}
      {schedulePipeline && (
        <ScheduleEditorDialog
          open={!!schedulePipeline}
          onOpenChange={(open) => {
            if (!open) {
              setSchedulePipeline(null);
              setSchedulePipelineId(null);
            }
          }}
          pipelineId={schedulePipeline.id}
          pipelineName={schedulePipeline.name}
          currentSchedule={schedulePipeline.schedule}
          onSave={async (config: ScheduleConfig) => {
            await updateScheduleMutation.mutateAsync(config);
            await refetch();
          }}
          onDelete={schedulePipeline.schedule ? async () => {
            await deleteScheduleMutation.mutateAsync();
            await refetch();
          } : undefined}
        />
      )}

      {/* Export Pipeline Dialog */}
      <PipelineExportDialog
        pipelineIds={Array.from(selectedIds)}
        pipelineNames={Array.from(selectedIds).map((id) =>
          pipelines.find((p) => p.id === id)?.name || id
        )}
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
      />

      {/* Import Pipeline Dialog */}
      <PipelineImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onSuccess={() => refetch()}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deletePipeline}
        onOpenChange={(open) => !open && setDeletePipeline(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Pipeline</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>"{deletePipeline?.name}"</strong>?
              This action cannot be undone. All pipeline configuration and execution history
              will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Pipeline'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog
        open={!!bulkDeletePipeline}
        onOpenChange={(open) => !open && setBulkDeletePipeline(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Pipelines</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{bulkDeletePipeline?.length} pipelines</strong>?
              This action cannot be undone. All pipeline configuration and execution history
              will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                `Delete ${bulkDeletePipeline?.length || 0} Pipeline${bulkDeletePipeline && bulkDeletePipeline.length > 1 ? 's' : ''}`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
